import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const REPORT_TYPES = [
  { id: "sales", label: "Vendas" },
  { id: "receivables", label: "Contas a Receber" },
  { id: "payments", label: "Recebimentos" },
  { id: "commissions", label: "Comissões" },
  { id: "bonuses", label: "Bonificações" },
  { id: "cash", label: "Fluxo de Caixa" },
  { id: "payables", label: "Contas a Pagar" },
  { id: "clients", label: "Clientes" },
];

export default function Reports() {
  const [kind, setKind] = useState("sales");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [vendedorId, setVendedorId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sellers, setSellers] = useState([]);
  const [clients, setClients] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/sellers"), api.get("/clients")]).then(([s, c]) => {
      setSellers(s.data); setClients(c.data);
    });
  }, []);

  const params = () => ({
    from_date: from, to_date: to,
    vendedor_id: vendedorId, cliente_id: clienteId, status: statusF,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${kind}`, { params: params() });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/reports/${kind}/export`, {
        params: { ...params(), format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${kind}_${from || "inicio"}_a_${to || "hoje"}.${format}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao gerar arquivo"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  return (
    <Layout title="Relatórios" subtitle="Exporte em PDF ou Excel com filtros">
      <Card className="p-4 rounded-sm mb-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs font-semibold">Relatório</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="report-kind" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{REPORT_TYPES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">De</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1"/></div>
          <div><Label className="text-xs font-semibold">Até</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1"/></div>
          <div>
            <Label className="text-xs font-semibold">Vendedor</Label>
            <Select value={vendedorId || "all"} onValueChange={(v) => setVendedorId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="report-vendedor" className="mt-1"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Cliente</Label>
            <Select value={clienteId || "all"} onValueChange={(v) => setClienteId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="report-cliente" className="mt-1"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="recebido">Recebido</SelectItem>
                <SelectItem value="parcial">Parcial</SelectItem>
                <SelectItem value="atrasado">Atrasado</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button onClick={load} data-testid="report-apply">{loading ? "Carregando..." : "Aplicar Filtros"}</Button>
          <div className="flex-1"/>
          <Button variant="outline" onClick={() => download("xlsx")} data-testid="report-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1"/>Excel
          </Button>
          <Button onClick={() => download("pdf")} data-testid="report-export-pdf">
            <FileText className="w-4 h-4 mr-1"/>PDF
          </Button>
        </div>
      </Card>

      {data && (
        <>
          <Card className="p-4 rounded-sm mb-4">
            <h3 className="font-display text-lg tracking-tight mb-3">{data.title}</h3>
            <div className="flex flex-wrap gap-6">
              {Object.entries(data.totals || {}).map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{k}</p>
                  <p className="kpi-num text-2xl mt-1">{typeof v === "number" && !["Qtd", "Total Clientes"].includes(k) ? fmtBRL(v) : v}</p>
                </div>
              ))}
            </div>
          </Card>
          <Card className="rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-secondary/60"><TableRow>
                  {data.columns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((row, i) => (
                    <TableRow key={i}>
                      {row.map((v, j) => {
                        const isMoney = (data.money_cols || []).includes(j);
                        return <TableCell key={j} className={isMoney ? "tabular-nums" : ""}>
                          {isMoney && typeof v === "number" ? fmtBRL(v) : (v === null || v === undefined ? "—" : String(v))}
                        </TableCell>;
                      })}
                    </TableRow>
                  ))}
                  {data.rows.length === 0 && (
                    <TableRow><TableCell colSpan={data.columns.length} className="text-center text-muted-foreground py-6">Nenhum registro encontrado</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
}
